// src/components/dashboard/DashboardCard.jsx
//
// Canvas-like tile card used on the dashboard.
// Now supports an optional "Open" link (to BookReader) via `openTo` and `openState`.
//
// Props:
// - title, genre, volume: display metadata
// - status: draft status (authors only)
// - role: "author" | "reviewer" | "reader" | "admin" | "designer" | "collaborator_design" | "collaborator_view"
// - onApprove: callback (Reviewer: Approve; Author on approved: Finalize)
// - openTo: string route to navigate to (e.g. "/read/:id" or "/review/:id")
// - openState: router state object to pass along (e.g. { book: { _id, title, pdfUrl } })

import React from 'react';
import { Link } from 'react-router-dom';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Typography } from '@mui/material';
import '../../styles/DashboardCard.css'; // fixed relative path to /src/styles

const DashboardCard = ({ title, genre, volume, status, role, onApprove, onReject, openTo, openState, versionName, inviteMessage }) => {
  const [rejectOpen, setRejectOpen] = React.useState(false);
  const [rejectNotes, setRejectNotes] = React.useState('');
  const [approveOpen, setApproveOpen] = React.useState(false);

  return (
    <article className="tile">
      {/* Header/thumbnail placeholder keeps tiles aligned even without images */}
      <div className="tile-thumb" />

      {/* Body */}
      <div className="tile-body">
        <h3 className="tile-title" title={title}>{title}</h3>
        <div className="tile-meta"><strong>Genre:</strong> {genre}</div>
        <div className="tile-meta"><strong>Volume:</strong> {volume}</div>

        {/* Status (only relevant/visible for authors) */}
        {role === 'author' && status && (
          <div className={`tile-status ${String(status).toLowerCase().replace('_','-')}`}>
            {String(status).replace('_',' ').replace(/\b\w/g, c => c.toUpperCase())}
          </div>
        )}
        
        {/* Designer badge */}
        {role === 'designer' && (
          <div className="tile-status" style={{ backgroundColor: '#8b5cf6', color: 'white' }}>
            🎨 Cover Designer
          </div>
        )}
        
        {/* Feedback reviewer badge */}
        {role === 'feedback_reviewer' && (
          <div className="tile-status" style={{ backgroundColor: '#0ea5e9', color: 'white' }}>
            👁️ Cover Feedback
          </div>
        )}

        {/* Collaborator Design badge */}
        {role === 'collaborator_design' && (
          <div className="tile-status" style={{ backgroundColor: '#8b5cf6', color: 'white' }}>
            🎨 Cover Design
          </div>
        )}

        {/* Collaborator View Only badge */}
        {role === 'collaborator_view' && (
          <div className="tile-status" style={{ backgroundColor: '#0ea5e9', color: 'white' }}>
            👁️ View & Feedback
          </div>
        )}

        {/* Version name for collaborator tasks */}
        {versionName && (role === 'collaborator_design' || role === 'collaborator_view') && (
          <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.25rem' }}>
            📌 {versionName}
          </div>
        )}

        {/* Invite message from author */}
        {inviteMessage && (role === 'collaborator_design' || role === 'collaborator_view') && (
          <div style={{
            marginTop: '0.5rem',
            padding: '0.5rem 0.6rem',
            backgroundColor: '#f8f7ff',
            borderRadius: '6px',
            border: '1px solid #e0dff4',
            borderLeft: '3px solid #8b5cf6',
            fontSize: '0.75rem',
            color: '#4a5568',
            fontStyle: 'italic',
            lineHeight: '1.4',
          }}>
            💬 {inviteMessage}
          </div>
        )}
      </div>

      {/* Actions: Open + role-specific action */}
      <div className="tile-actions">
        {/* Open in BookReader (only shown when a target route is provided) */}
        {openTo && (
          <Link className="tile-btn" to={openTo} state={openState}>
            {role === 'designer' || role === 'collaborator_design' ? 'Design Cover' : role === 'feedback_reviewer' || role === 'collaborator_view' ? 'View Cover' : 'Open'}
          </Link>
        )}

        {/* Reviewer can approve from the card */}
        {role === 'reviewer' && onApprove && (
          <button className="tile-btn" onClick={() => setApproveOpen(true)}>Approve</button>
        )}
        {role === 'reviewer' && onReject && (
          <button className="tile-btn" style={{ backgroundColor: '#dc2626' }} onClick={() => setRejectOpen(true)}>Reject</button>
        )}

        {/* Lead reviewer can approve/reject from the card */}
        {role === 'lead_reviewer' && onApprove && (
          <button className="tile-btn" onClick={onApprove}>Approve</button>
        )}
        {role === 'lead_reviewer' && onReject && (
          <button className="tile-btn" style={{ backgroundColor: '#dc2626' }} onClick={() => setRejectOpen(true)}>Reject</button>
        )}

        {/* Author can finalize an approved draft */}
        {role === 'author' && status === 'approved' && onApprove && (
          <button className="tile-btn" onClick={onApprove}>Finalize &amp; Publish</button>
        )}
      </div>

      {/* Approve confirmation */}
      <Dialog open={approveOpen} onClose={() => setApproveOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Approve "{title}"?</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary">
            This will mark the manuscript as approved and notify the author.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setApproveOpen(false)}>Cancel</Button>
          <Button variant="contained" color="success" onClick={() => { onApprove(); setApproveOpen(false); }}>
            Confirm Approval
          </Button>
        </DialogActions>
      </Dialog>

      {/* Reject modal */}
      <Dialog open={rejectOpen} onClose={() => { setRejectOpen(false); setRejectNotes(''); }} maxWidth="sm" fullWidth>
        <DialogTitle>Reject "{title}"</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            multiline
            rows={4}
            label="Feedback for author (optional)"
            placeholder="Explain what needs to be revised..."
            value={rejectNotes}
            onChange={e => setRejectNotes(e.target.value)}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setRejectOpen(false); setRejectNotes(''); }}>Cancel</Button>
          <Button variant="contained" color="error" onClick={() => { onReject(rejectNotes); setRejectNotes(''); setRejectOpen(false); }}>
            Confirm Rejection
          </Button>
        </DialogActions>
      </Dialog>
    </article>
  );
};

export default DashboardCard;
